/**
 * Человеческие формулировки отказов камеры и микрофона.
 *
 * Браузер отдаёт `NotAllowedError` и подобное — служебные имена на
 * английском. Показывать их пользователю нельзя: он видит «Permission
 * denied» и не понимает, что делать. Формулировки собраны в одном месте,
 * потому что нужны и на экране подготовки, и во время встречи — раньше
 * во встрече показывалось сырое сообщение браузера.
 */

/** Родительный падеж: «камере», «микрофону». */
export type DeviceName = 'камере' | 'микрофону';

/**
 * Имя ошибки читаем «по-утиному», а не через `instanceof DOMException`.
 *
 * Через LiveKit исходный отказ приходит завёрнутым в собственный класс
 * ошибки — проверка на `DOMException` его не узнаёт, и вместо объяснения
 * человек видел бы общую отписку. Имя же (`NotAllowedError` и прочие)
 * при заворачивании сохраняется.
 */
function errorName(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'name' in err) {
    const name = (err as { name?: unknown }).name;
    if (typeof name === 'string') return name;
  }
  return '';
}

export function describeDeviceError(err: unknown, device: DeviceName): string {
  const name = errorName(err);

  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return `Доступ к ${device} запрещён. Разрешите его в настройках браузера.`;
  }

  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return `Устройство не найдено: нет доступа к ${device}.`;
  }

  if (name === 'NotReadableError' || name === 'AbortError') {
    return `Не удалось обратиться к ${device}: возможно, устройство занято другим приложением.`;
  }

  return `Не удалось включить ${device === 'камере' ? 'камеру' : 'микрофон'}.`;
}
